import { Component, Input, OnDestroy, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { WebSolveupLibraryService } from '@mare-common/web-solveup-library';
import { EventData, EventInteractionService } from '@mare-common/ngx-mare-common';
import { CommonConfig } from '../common-config/common-config';
import { catchError, Subject, take, takeUntil } from 'rxjs';
import { environment } from 'src/environments/environment';
import { FormGroup, Validators } from '@angular/forms';
import { NgxDynamicFormDataInputElement } from '@mare-common/ngx-dynamic-form';
import { PlatformApiWpService } from 'src/app/services/platform-apiwp.service';
import { UserService } from 'src/app/services/user.service';
import { MareAuthService } from '@mare-common/ngx-mare-auth';
import { StartChatConfig } from '@mare-common/web-solveup-library/chat-service';
import { NgxAssistanceOpenFormData } from '@mare-common/ngx-assistance-form';
import { UserCompanyService } from 'src/app/services/userCompany.service';
import { Message } from '@mare-common/ngx-mare-chat';
import { MatButtonModule } from '@angular/material/button';

export interface UserContactInfo {
  name: string;
  email: string;
  businessName?: string;
  phone?: string;
  piva?: string;
  message?: string;
  productName?: string;
}
export interface ProductContactInfo {
  name: string;
  id: string;
  sku: string;
}
export interface ContactInfo {
  user: UserContactInfo;
  product?: ProductContactInfo;
}


@Component({
  selector: 'app-button-contact',
  standalone: true,
  imports: [CommonModule,
    TranslateModule,
    MatButtonModule
  ],
  templateUrl: './button-contact.component.html',
  styleUrls: ['./button-contact.component.scss']
})
export class ButtonContactComponent implements OnInit, OnDestroy {
  @Input() piva!: string;
  @Input() company!: any;
  @Input() buttonfw: boolean = false;
  @Input() buttonlabel: string = 'Richiedi informazioni';
  @Input() buttonclass?: string | string[];
  @Input() wrapperclass: string = 'ask-contact-wrapper';
  @Input() buttonType: 'stroked' | 'raised' | 'flat' | 'icon' | 'base' = 'flat';
  // @Input() userinfo!: any;
  @Input() product?: ProductContactInfo;
  @Input() contactInfo!: UserContactInfo;

  checkingForAfChannel$ = signal<string | null>(null);
  checkLoggedUser$ = signal<boolean>(this.authService.isLogged());
  subtitleMessageStaticAssistanceForm!: string;
  private unsubscribe$ = new Subject<void>();

  constructor(
    private translateService: TranslateService,
    private webSolveupLibraryService: WebSolveupLibraryService,
    private eventInteractionService: EventInteractionService,
    private commonConfig: CommonConfig,
    private platformApiWpService: PlatformApiWpService,
    private userCompanyService: UserCompanyService,
    private authService: MareAuthService,) {
    translateService.setDefaultLang('it');
    translateService.use('it');

    this.eventInteractionService.getObservable().subscribe((event: EventData) => {
      // console.log(event)
      switch (event.event) {
        case 'closeAssistanceForm':
          this.checkingForAfChannel$.set(null);
          break;
        default:
          break;
      }
    });
  }
  ngOnInit(): void {
    // this.checkButtonClasses();
    if (this.checkIsworkingTime()) {
      this.translateService.get('assistance_form_subtitle_workingtime').pipe(take(1)).subscribe((res) => {
        this.subtitleMessageStaticAssistanceForm = res
      })
    } else {
      this.translateService.get('assistance_form_subtitle_offtime').pipe(take(1)).subscribe((res) => {
        this.subtitleMessageStaticAssistanceForm = res
      })
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  checkButtonClasses() {
    if (!this.buttonclass){
      this.buttonclass = [];
    }
    let localClasses;
    if (this.buttonType) {
      switch (this.buttonType) {
        case 'raised':
        localClasses = ['mat-mdc-button', 'mat-mdc-raised-button'];
          break;
        case 'stroked':
        localClasses = ['mdc-button--outlined','mat-mdc-outlined-button'];
          break;
        case 'flat':
        localClasses = ['mat-mdc-unelevated-button'];
          break;
        case 'icon':
        localClasses = ['mat-mdc-icon-button'];
          break;
        default:
          break;
      }
    }
    if (localClasses) {
      if(Array.isArray(this.buttonclass)){
        this.buttonclass = this.buttonclass.concat(localClasses);
      } else {
        this.buttonclass += localClasses.join(' ');
      }
    }
  }

  /**
   * Function to check if it's a working time from 9:00 to 18:00 and Monday to Friday.
   *
   * @param {Date} date - optional: if not provided, current date is got
   * @return {boolean} if true is working time
   */
  checkIsworkingTime(date?: Date) {
    if (!date) {
      date = new Date();
    }
    const day = date.getDay();
    const hour = date.getHours();

    const isWorkingDay = day >= this.commonConfig.getWorkingTime().daystart && day <= this.commonConfig.getWorkingTime().dayend;
    // Controlla se l'ora è all'interno dell'intervallo di lavoro
    const isWorkingTime = hour >= this.commonConfig.getWorkingTime().hourstart && hour <= this.commonConfig.getWorkingTime().hourend;

    if (isWorkingDay && isWorkingTime) {
      return true
    }
    return false

  }

  getReportSubmit(forceDefaultValue: boolean = false) {
    // console.log(this.myInformationValue);
    this.checkingForAfChannel$.set('In connessione ...');

    // let contactInfo: UserContactInfo = this.contactInfo;
    if (this.checkIsworkingTime()) {
      this.webSolveupLibraryService.getIsAvailableOperators().then(async (isAvailable: boolean) => {
        // console.log('is available', isAvailable)
        if (isAvailable) {

          this.checkingForAfChannel$.set('livechat');
          let tags: string[] = [];

          let piva = this.piva ? this.piva : (this.contactInfo && this.contactInfo.piva ? this.contactInfo.piva : 'no piva');
          if (piva) {
            tags.push('piva: ' + piva);
          }

          if (this.authService.isLogged()) {
            const userAndCompanyInfo = await this.userCompanyService.retrieveUserCompany();
            if (userAndCompanyInfo) {
              let user = userAndCompanyInfo.user;
              if (user?.email)
                tags.push('email: ' + user.email)

              if (user?.phone)
                tags.push('tel: ' + user.phone)

              if (user?.name && user?.surname)
                tags.push('utente: ' + user.name + " " + user.surname)
            }
            let company = userAndCompanyInfo.company;
            if (company) {
              if (company.businessName) {
                tags.push('azienda: ' + company.businessName);
              }
            }
          } else if (this.contactInfo) {
            tags.push('utente: ' + this.contactInfo.name );
            tags.push('email: ' + this.contactInfo.email);
            tags.push('tel: ' + this.contactInfo.phone);
          }
          if (this.product) {
            tags.push('prodotto: ' + this.product.name);
          }
          const startChatConfig: StartChatConfig = this.commonConfig.getFromConfig('webSolveupStartChatConfig', {
            title: "Conferma",
            subtitle: undefined,
            panelClass: undefined,
            content: '<p>Il consulente vorrebbe contattarti in videochiamata per fornirti maggiori informazioni</p>',
            cancelButtonLabel: 'No',
            cancelButtonColor: 'none',
            acceptButtonLabel: 'OK, avvia una videochiamata',
            acceptButtonColor: 'primary'
          });


          let data: NgxAssistanceOpenFormData = {
            forceOpenForm: false,
            tryUseSolveup: true,
            tryUseQuestionnaire: false,
            askUserToSolveup: false, // flag che se true mostra l'intro dell'assistance form
            tryUseSolveupConfig: {
              tags: tags,
              showFullscreen: true
            },
            webSolveupConfig: {
              handleOnSuccessMessage: "Attendi un operatore: ti risponderà via chat appena possibile",
              unloadMessage: "Sei sicuro di voler chiudere la pagina?",
              closedEmergencyMessageByOperator: "Grazie! Non esitare a ricontattarci.",
              defaultWaitMessage: "Attendere prego...",
              noUserAvailable: "Non ci sono utenti disponibili",
              noOperatorsAvailable: "Non ci sono operatori disponibili",
              genericErrorMessage: "Errore durante la richiesta",
              operatorStartsCallMessage: "Un consulente è entrato in chiamata",
              closedEmergencyMessageByUser: "Grazie! Non esitare a ricontattarci.",
              linkfyEnabled: false,
              enableAudio: true,
              enableVideo: true,
              tryStartChat: true,
              startChatConfig: startChatConfig
            },
            onClickedMessage: (event: { event: any, message: Message }) => {
              console.log(event);
              let evt: Event = event.event;
              evt.stopImmediatePropagation();
              if (event.message.message.indexOf('http') > -1) {
                let url: any = new URL(event.message.message);
                url = url.pathname + url.search;
                this.eventInteractionService.publishData({ event: 'gotoLink', data: { url: url } });
                this.eventInteractionService.publishData({ event: 'setVideocallFullscreen', data: false })
              }
            }
          }
          this.eventInteractionService.publishData({ event: 'openAssistanceForm', data: data });
        } else {
          this.checkingForAfChannel$.set('offline');
          this.manageNotifyOperatorsForRequest()
          this.startForm(this.contactInfo)
        }
      }).catch((err) => {
        console.log(err);
        this.checkingForAfChannel$.set('offline');
        this.manageNotifyOperatorsForRequest()
        this.startForm(this.contactInfo)
      })
    } else {
      this.checkingForAfChannel$.set('offline');
      this.startForm(this.contactInfo)
    }
  }

  async startForm(userCompanyInfo?: UserContactInfo) {
    if (!userCompanyInfo) {
      await this.userCompanyService.retrieveUserCompany().then((res: any) => {
        userCompanyInfo = res
      });
    }
    let formGroup = new FormGroup({});

    const dataInput = this.getDataInputs({
      name: userCompanyInfo?.name ?? '',
      email: userCompanyInfo?.email ?? '',
      phone: userCompanyInfo?.phone ?? '',
      piva: this.piva ?? userCompanyInfo?.piva ?? '',
      productId: userCompanyInfo?.productName ?? '',

    });

    let tags: string[] = [];
    if (userCompanyInfo) {
      if (userCompanyInfo.email)
        tags.push('email: ' + userCompanyInfo.email)

      if (userCompanyInfo.phone)
        tags.push('tel: ' + userCompanyInfo.phone)

      if (userCompanyInfo.name)
        tags.push('utente: ' + userCompanyInfo.name)

      if (userCompanyInfo.businessName)
        tags.push('azienda: ' + userCompanyInfo.businessName)

      if (userCompanyInfo.piva)
        tags.push('piva: ' + userCompanyInfo.piva)
    }


    let data: NgxAssistanceOpenFormData = {
      forceOpenForm: true,
      tryUseSolveup: false,
      tryUseQuestionnaire: false,
      localQuestionnaireConfig: undefined,
      staticForm: {
        title: 'Richiedi informazioni',
        subtitle: this.subtitleMessageStaticAssistanceForm,
      },
      dynamicFormConfig: {
        isDynamicFormEnabled: true,
        preventSendForm: true,
        dynamicFormConfig: {
          // topHeader: 'Richiedi informazioni',
          // header: 'Un operatore non è attualmente disponibile. Compila in form qui sotto e sarai ricontattato il prima possibile.',
          formGroup: formGroup,
          dataInput: dataInput,
          elements: dataInput,
          displayedColumns: [],
          formWrapperClass: 'af-form-assistance-form pt--32',
          formClass: 'form-class-1 form-class-2',
          template: 'GRID',
          buttons: [
            {
              color: "primary",
              hasIcon: false,
              label: 'Invia',
              closeDialog: false,
              action: (par: any) => { this.inviaButtonClicked(formGroup) },
              disabledIfFunction: (event: any) => {
                return !formGroup.valid
              }
            },
            {
              color: "accent",
              hasIcon: false,
              label: 'Annulla',
              closeDialog: false,
              action: (par: any) => { this.annullaButtonClicked(formGroup) },
            }
          ]
        }
      },
      askUserToSolveup: false,
      tryUseSolveupConfig: undefined,
      webSolveupConfig: {
        handleOnSuccessMessage: "Attendi un operatore: ti risponderà via chat appena possibile",
        unloadMessage: "Sei sicuro di voler chiudere la pagina?",
        closedEmergencyMessageByOperator: "L'operatore ha terminato la sessione",
        defaultWaitMessage: "Attendere prego...",
        noUserAvailable: "Non ci sono utenti disponibili",
        noOperatorsAvailable: "Non ci sono operatori disponibili",
        genericErrorMessage: "Errore durante la richiesta",
        operatorStartsCallMessage: "Un operatore è entrato in chiamata",
        closedEmergencyMessageByUser: "Hai terminato la sessione",
        linkfyEnabled: false,
        enableAudio: true,
        enableVideo: true,
        tryStartChat: false
      }
    }

    /*
    *****************************
    * Apriamo il form di contatto
    *****************************
    */
    this.eventInteractionService.publishData({
      event: 'openAssistanceForm',
      data: data
    })
  }

  inviaButtonClicked(par: any) {
    this.submitForm(par.value).then((res: any) => {

      this.eventInteractionService.publishData({
        event: 'showRecap',
        data: true
      })
      this.checkingForAfChannel$.set(null);
    })
  }
  public submitForm(body: any): Promise<any> {
   
    return new Promise((resolve, reject) => {

      const wpbody = {
        _wpcf7: environment.wpAssistanceFormId,
        "assistance-name": body.name,
        "assistance-email": body.email,
        "assistance-vatNumber": body.piva,
        "assistance-phone": body.phone,
        "assistance-type": body.type ?? 'commerciale',
        "assistance-message": body.message
      }
      this.platformApiWpService.postRequest('msc/v1/submitForm', wpbody).then((sub: any) => {
        sub
          .pipe(
            catchError((err: any) => {
              reject(err);
              console.log(err);
              return err;
            })
          )
          .subscribe((data: any) => {
            // console.log(data);
            resolve(data);
          });
      });
    });
  }

  annullaButtonClicked(par: any) {
    // console.log(par);
    this.eventInteractionService.publishData({
      event: 'closeAssistanceForm'
    })
    this.checkingForAfChannel$.set(null);
  }

  async manageNotifyOperatorsForRequest() {

    if (this.commonConfig.getFromConfig('disableNotifyLostRequestestInWorkingTime')) {
      return;
    }

    this.userCompanyService.retrieveUserCompany().then((res) => {
      if (!res) {
        return;
      }
      const user = res.user;
      const company = res.company;
      const request = {
        user_name: user?.name + " " + user?.surname,
        company: company?.business_name ?? '--',
        email: user?.email,
        product: this.product?.name ?? '--',
        timestamp: new Date().toISOString(),
      }
      this.platformApiWpService.postRequest('/msc/v1/notifyRequestOffline', request, false, true).then((sub) => {
        sub.pipe(take(1)).subscribe((ret) => {
          // qui riceviamo la risposta di wordpress alla funzione di wp_mail (success/fail);
          console.info(ret);
        })

      })

    })
  }


  getDataInputs(data?: {
    name?: string,
    email?: string,
    phone?: string,
    businessName?: string,
    productId?: string,
    piva?: string
  }): Array<NgxDynamicFormDataInputElement> {
    return [
      {
        type: 'TEXT',
        formControlName: 'name',
        id: 'name',
        placeholder: 'Nome',
        label: 'Nome',
        required: true,
        value: data?.name
      },
      {
        type: 'TEXT',
        formControlName: 'email',
        id: 'email',
        placeholder: 'Email',
        label: 'Email',
        required: true,
        value: data?.email,
        validators: [
          // eslint-disable-next-line no-useless-escape
          Validators.required, Validators.pattern(/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/)
        ]
      },
      {
        type: 'TEXT',
        formControlName: 'piva',
        id: 'piva',
        placeholder: 'piva',
        label: 'piva',
        required: true,
        value: data?.piva
      },
      {
        type: 'TEXT',
        formControlName: 'phone',
        id: 'phone',
        placeholder: 'Telefono',
        label: 'Telefono',
        value: data?.phone,
        required: true,
      },
      {
        type: 'TEXTAREA',
        formControlName: 'message',
        id: 'message',
        placeholder: 'Messaggio',
        label: 'Cosa vuoi chiederci',
        value: 'Sono interessato ad avere maggiori informazioni'+ (data?.productId ? ' su *' + data.productId +'*' : ''),
        required: true,
      },
      {
        type: 'CHECK',
        formControlName: 'acceptance',
        id: 'acceptance',
        required: true,
        placeholder: 'Accetto che i miei dati inviati, vengano raccolti e archiviati per l\'uso strettamente necessario alle finalità del presente form (obbligatorio)',
        validators: [Validators.requiredTrue],
        
      },
      // {
      //   type: 'TEXT',
      //   formControlName: 'businessName',
      //   id: 'businessName',
      //   placeholder: 'Nome azienda',
      //   label: 'Azienda',
      //   required: true,
      //   isVisible: false,
      //   visibleIf: {
      //     formControlName: 'email',
      //     checkFunction: (data: any) => { return false}
      //     },
      //   value: data?.businessName
      // },
     
      // {
      //   type: 'TEXT',
      //   formControlName: 'productId',
      //   id: 'productIdpiva',
      //   placeholder: 'Prodotto / Servizio',
      //   label: 'Prodotto / Servizio',
      //   required: true,
      //   isVisible: false,
      //   visibleIf: {
      //     formControlName: 'email',
      //     checkFunction: (data: any) => { return false}
      //     },
      //   value: data?.piva
      // },

    ]
  };

}
